import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './GoogleBusiness.css';

interface GoogleBusinessProps {
  storeId: string;
  storeName: string;
}

interface AnalyticsPixels {
  googleTagManagerId?: string;
  googleAdsConversionId?: string;
  ga4MeasurementId?: string;
}

interface BusinessInfo {
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  phoneNumber: string;
  website: string;
  hours: {
    [key: string]: {
      open: string;
      close: string;
      isClosed: boolean;
    };
  };
  categories: string[];
  description: string;
}

interface AnalyticsData {
  impressions: number;
  clicks: number;
  calls: number;
  directions: number;
  websiteVisits: number;
  reviews: {
    average: number;
    total: number;
  };
  photos: {
    viewsTotal: number;
    photoCount: number;
  };
}

export const GoogleBusiness: React.FC<GoogleBusinessProps> = ({ storeId, storeName }) => {
  const { getIdToken } = useAuth();
  const [activeSection, setActiveSection] = useState<'pixels' | 'profile' | 'analytics'>('pixels');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for analytics pixels
  const [pixels, setPixels] = useState<AnalyticsPixels>({});
  const [savedPixels, setSavedPixels] = useState<AnalyticsPixels>({});
  const [expandedInstructions, setExpandedInstructions] = useState<Record<string, boolean>>({});
  
  // State for business info
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    name: storeName,
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    phoneNumber: '',
    website: '',
    hours: {
      monday: { open: '09:00', close: '17:00', isClosed: false },
      tuesday: { open: '09:00', close: '17:00', isClosed: false },
      wednesday: { open: '09:00', close: '17:00', isClosed: false },
      thursday: { open: '09:00', close: '17:00', isClosed: false },
      friday: { open: '09:00', close: '17:00', isClosed: false },
      saturday: { open: '10:00', close: '15:00', isClosed: false },
      sunday: { open: '10:00', close: '15:00', isClosed: true }
    },
    categories: [],
    description: ''
  });
  
  // State for analytics data
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [selectedMenuItems, setSelectedMenuItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchData();
  }, [storeId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      
      // Fetch analytics pixels
      const pixelsResponse = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/pixels/${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (pixelsResponse.ok) {
        const pixelsData = await pixelsResponse.json();
        setPixels(pixelsData.data || {});
        setSavedPixels(pixelsData.data || {});
      }
      
      // Fetch business info
      const businessResponse = await fetch(`${API_ENDPOINTS.stores()}/${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (businessResponse.ok) {
        const storeData = await businessResponse.json();
        if (storeData.data) {
          setBusinessInfo(prev => ({
            ...prev,
            name: storeData.data.name || storeName,
            address: storeData.data.address || prev.address,
            phoneNumber: storeData.data.phoneNumber || '',
            website: storeData.data.website || '',
            hours: storeData.data.hours || prev.hours,
            description: storeData.data.description || ''
          }));
        }
      }
      
      // Check Google connection status
      const googleAuthResponse = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/google/status/${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (googleAuthResponse.ok) {
        const authData = await googleAuthResponse.json();
        setIsGoogleConnected(authData.isConnected || false);
        
        // If connected, fetch analytics
        if (authData.isConnected) {
          fetchAnalytics();
        }
      }
      
      // Fetch menu items
      const menuResponse = await fetch(`${API_ENDPOINTS.menuItems()}?storeId=${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (menuResponse.ok) {
        const menuData = await menuResponse.json();
        setMenuItems(menuData.data || []);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAnalytics()}/google/${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data.analytics);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const activateDemoMode = () => {
    setIsDemoMode(true);
    setIsGoogleConnected(true);
    setBusinessInfo(prev => ({
      ...prev,
      name: storeName || prev.name,
    }));
    setAnalyticsData({
      impressions: 12450,
      clicks: 843,
      calls: 127,
      directions: 312,
      websiteVisits: 567,
      reviews: {
        average: 4.3,
        total: 89
      },
      photos: {
        viewsTotal: 4230,
        photoCount: 24
      }
    });
    setSuccess('Demo mode activated - explore the full Google Business interface with sample data');
    setTimeout(() => setSuccess(null), 4000);
  };

  const exitDemoMode = () => {
    setIsDemoMode(false);
    setIsGoogleConnected(false);
    setAnalyticsData(null);
  };

  const handleGoogleAuth = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/google/auth`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ storeId, redirectUri: window.location.href })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authUrl;
      } else {
        throw new Error('Failed to initiate Google authentication');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    }
  };

  const savePixels = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/pixels/${storeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pixels)
      });
      
      if (response.ok) {
        setSavedPixels(pixels);
        setSuccess('Analytics pixels saved successfully');
      } else {
        throw new Error('Failed to save pixels');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pixels');
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessProfile = async () => {
    if (!isGoogleConnected) {
      setError('Please connect your Google Business account first');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_ENDPOINTS.socialMediaAccounts()}/google/business/${storeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessInfo,
          menuItems: Array.from(selectedMenuItems).map(id => 
            menuItems.find(item => item.menuItemId === id)
          ).filter(Boolean)
        })
      });
      
      if (response.ok) {
        setSuccess('Google Business Profile updated successfully');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleInstructions = (field: string) => {
    setExpandedInstructions(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const renderPixelsSection = () => (
    <div className="pixels-section">
      <h3>Analytics & Tracking Pixels</h3>
      <p className="section-description">
        Configure your analytics and tracking pixels to monitor your restaurant's online performance across platforms.
      </p>
      
      <div className="pixels-form">
        <div className="form-group">
          <div className="pixel-label-row">
            <label htmlFor="gtm">Google Tag Manager Container ID</label>
            <button className="help-toggle" onClick={() => toggleInstructions('gtm')}>
              {expandedInstructions['gtm'] ? 'Hide' : 'How to find this'}
            </button>
          </div>
          <input
            id="gtm"
            type="text"
            placeholder="GTM-XXXXXX"
            value={pixels.googleTagManagerId || ''}
            onChange={(e) => setPixels({ ...pixels, googleTagManagerId: e.target.value })}
          />
          <small>Used for advanced tracking and tag management</small>
          {expandedInstructions['gtm'] && (
            <div className="pixel-instructions">
              <ol>
                <li>Go to <strong>tagmanager.google.com</strong></li>
                <li>Sign in with your Google account</li>
                <li>Select your container (or create one)</li>
                <li>The Container ID is displayed at the top of the page (format: GTM-XXXXXXX)</li>
              </ol>
            </div>
          )}
        </div>

        <div className="form-group">
          <div className="pixel-label-row">
            <label htmlFor="ga4">GA4 Measurement ID</label>
            <button className="help-toggle" onClick={() => toggleInstructions('ga4')}>
              {expandedInstructions['ga4'] ? 'Hide' : 'How to find this'}
            </button>
          </div>
          <input
            id="ga4"
            type="text"
            placeholder="G-XXXXXXXXXX"
            value={pixels.ga4MeasurementId || ''}
            onChange={(e) => setPixels({ ...pixels, ga4MeasurementId: e.target.value })}
          />
          <small>Google Analytics 4 for website analytics</small>
          {expandedInstructions['ga4'] && (
            <div className="pixel-instructions">
              <ol>
                <li>Go to <strong>analytics.google.com</strong></li>
                <li>Click Admin (gear icon) in the bottom left</li>
                <li>Under Property, click <strong>Data Streams</strong></li>
                <li>Select your web stream</li>
                <li>The Measurement ID is at the top right (format: G-XXXXXXXXXX)</li>
              </ol>
            </div>
          )}
        </div>

        <div className="form-group">
          <div className="pixel-label-row">
            <label htmlFor="gads">Google Ads Conversion ID</label>
            <button className="help-toggle" onClick={() => toggleInstructions('gads')}>
              {expandedInstructions['gads'] ? 'Hide' : 'How to find this'}
            </button>
          </div>
          <input
            id="gads"
            type="text"
            placeholder="AW-XXXXXXXXX"
            value={pixels.googleAdsConversionId || ''}
            onChange={(e) => setPixels({ ...pixels, googleAdsConversionId: e.target.value })}
          />
          <small>Track conversions from Google Ads campaigns</small>
          {expandedInstructions['gads'] && (
            <div className="pixel-instructions">
              <ol>
                <li>Go to <strong>ads.google.com</strong></li>
                <li>Click Tools & Settings in the top menu</li>
                <li>Under Measurement, click <strong>Conversions</strong></li>
                <li>Select a conversion action</li>
                <li>The Conversion ID is in the tag setup (format: AW-XXXXXXXXX)</li>
              </ol>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            className="save-btn"
            onClick={savePixels}
            disabled={saving || JSON.stringify(pixels) === JSON.stringify(savedPixels)}
          >
            {saving ? 'Saving...' : 'Save Pixels'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderProfileSection = () => (
    <div className="profile-section">
      <h3>Google Business Profile Management</h3>
      
      {!isGoogleConnected ? (
        <div className="google-connect-prompt">
          <i className="fab fa-google google-icon"></i>
          <h4>Connect Google Business Account</h4>
          <p>Connect your Google Business account to manage your business profile directly from this dashboard.</p>
          <button className="google-connect-btn" onClick={handleGoogleAuth}>
            <i className="fab fa-google"></i>
            Connect Google Account
          </button>
          <div className="demo-mode-option">
            <p className="demo-mode-divider">or</p>
            <button className="demo-mode-btn" onClick={activateDemoMode}>
              <i className="fas fa-flask"></i>
              Try Demo Mode
            </button>
            <p className="demo-mode-hint">Explore the full interface with sample data</p>
          </div>
        </div>
      ) : (
        <div className="profile-form">
          <div className="connected-status">
            <i className="fas fa-check-circle"></i>
            <span>Google Business account connected</span>
            {isDemoMode && <span className="demo-badge">(Demo)</span>}
            {isDemoMode && (
              <button className="exit-demo-btn" onClick={exitDemoMode}>
                Exit Demo Mode
              </button>
            )}
          </div>
          
          <div className="form-section">
            <h4>Basic Information</h4>
            
            <div className="form-group">
              <label htmlFor="business-name">Business Name</label>
              <input
                id="business-name"
                type="text"
                value={businessInfo.name}
                onChange={(e) => setBusinessInfo({ ...businessInfo, name: e.target.value })}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                value={businessInfo.phoneNumber}
                onChange={(e) => setBusinessInfo({ ...businessInfo, phoneNumber: e.target.value })}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="url"
                value={businessInfo.website}
                onChange={(e) => setBusinessInfo({ ...businessInfo, website: e.target.value })}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Business Description</label>
              <textarea
                id="description"
                rows={4}
                value={businessInfo.description}
                onChange={(e) => setBusinessInfo({ ...businessInfo, description: e.target.value })}
                placeholder="Describe your restaurant..."
              />
            </div>
          </div>
          
          <div className="form-section">
            <h4>Address</h4>
            
            <div className="form-group">
              <label htmlFor="street">Street Address</label>
              <input
                id="street"
                type="text"
                value={businessInfo.address.street}
                onChange={(e) => setBusinessInfo({
                  ...businessInfo,
                  address: { ...businessInfo.address, street: e.target.value }
                })}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  value={businessInfo.address.city}
                  onChange={(e) => setBusinessInfo({
                    ...businessInfo,
                    address: { ...businessInfo.address, city: e.target.value }
                  })}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  id="state"
                  type="text"
                  value={businessInfo.address.state}
                  onChange={(e) => setBusinessInfo({
                    ...businessInfo,
                    address: { ...businessInfo.address, state: e.target.value }
                  })}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="zip">ZIP Code</label>
                <input
                  id="zip"
                  type="text"
                  value={businessInfo.address.postalCode}
                  onChange={(e) => setBusinessInfo({
                    ...businessInfo,
                    address: { ...businessInfo.address, postalCode: e.target.value }
                  })}
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h4>Hours of Operation</h4>
            
            <div className="hours-grid">
              {Object.entries(businessInfo.hours).map(([day, hours]) => (
                <div key={day} className="hours-row">
                  <div className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                  
                  <label className="closed-toggle">
                    <input
                      type="checkbox"
                      checked={hours.isClosed}
                      onChange={(e) => setBusinessInfo({
                        ...businessInfo,
                        hours: {
                          ...businessInfo.hours,
                          [day]: { ...hours, isClosed: e.target.checked }
                        }
                      })}
                    />
                    <span>Closed</span>
                  </label>
                  
                  {!hours.isClosed && (
                    <>
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) => setBusinessInfo({
                          ...businessInfo,
                          hours: {
                            ...businessInfo.hours,
                            [day]: { ...hours, open: e.target.value }
                          }
                        })}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) => setBusinessInfo({
                          ...businessInfo,
                          hours: {
                            ...businessInfo.hours,
                            [day]: { ...hours, close: e.target.value }
                          }
                        })}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="form-section">
            <h4>Menu Items</h4>
            <p className="section-description">Select menu items to display on Google Business Profile</p>
            
            <div className="menu-items-grid">
              {menuItems.map(item => (
                <label key={item.menuItemId} className="menu-item-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedMenuItems.has(item.menuItemId)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedMenuItems);
                      if (e.target.checked) {
                        newSelected.add(item.menuItemId);
                      } else {
                        newSelected.delete(item.menuItemId);
                      }
                      setSelectedMenuItems(newSelected);
                    }}
                  />
                  <span>{item.name} - ${item.price}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              className="save-btn"
              onClick={updateBusinessProfile}
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Google Business Profile'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderAnalyticsSection = () => (
    <div className="analytics-section">
      <h3>Internet Presence Analytics</h3>
      
      {!isGoogleConnected ? (
        <div className="google-connect-prompt">
          <i className="fab fa-google google-icon"></i>
          <h4>Connect Google Business Account</h4>
          <p>Connect your Google Business account to view analytics and insights.</p>
          <button className="google-connect-btn" onClick={handleGoogleAuth}>
            <i className="fab fa-google"></i>
            Connect Google Account
          </button>
          <div className="demo-mode-option">
            <p className="demo-mode-divider">or</p>
            <button className="demo-mode-btn" onClick={activateDemoMode}>
              <i className="fas fa-flask"></i>
              Try Demo Mode
            </button>
          </div>
        </div>
      ) : analyticsData ? (
        <div className="analytics-dashboard">
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-eye"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.impressions.toLocaleString()}</div>
                <div className="analytics-label">Search Impressions</div>
                <div className="analytics-trend">Last 30 days</div>
              </div>
            </div>
            
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-mouse-pointer"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.clicks.toLocaleString()}</div>
                <div className="analytics-label">Total Clicks</div>
                <div className="analytics-trend">From search results</div>
              </div>
            </div>
            
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-phone"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.calls.toLocaleString()}</div>
                <div className="analytics-label">Phone Calls</div>
                <div className="analytics-trend">Direct from Google</div>
              </div>
            </div>
            
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-directions"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.directions.toLocaleString()}</div>
                <div className="analytics-label">Direction Requests</div>
                <div className="analytics-trend">To your location</div>
              </div>
            </div>
            
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-globe"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.websiteVisits.toLocaleString()}</div>
                <div className="analytics-label">Website Visits</div>
                <div className="analytics-trend">From your listing</div>
              </div>
            </div>
            
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-star"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.reviews.average.toFixed(1)}</div>
                <div className="analytics-label">Average Rating</div>
                <div className="analytics-trend">{analyticsData.reviews.total} total reviews</div>
              </div>
            </div>
            
            <div className="analytics-card">
              <div className="analytics-icon">
                <i className="fas fa-camera"></i>
              </div>
              <div className="analytics-content">
                <div className="analytics-value">{analyticsData.photos.viewsTotal.toLocaleString()}</div>
                <div className="analytics-label">Photo Views</div>
                <div className="analytics-trend">{analyticsData.photos.photoCount} photos</div>
              </div>
            </div>
          </div>
          
          <div className="analytics-insights">
            <h4>Performance Insights</h4>
            <div className="insights-grid">
              <div className="insight-card">
                <i className="fas fa-chart-line"></i>
                <div className="insight-content">
                  <h5>Search Performance</h5>
                  <p>Your listing appeared {analyticsData.impressions.toLocaleString()} times in search results with a {((analyticsData.clicks / analyticsData.impressions) * 100).toFixed(1)}% click-through rate.</p>
                </div>
              </div>
              
              <div className="insight-card">
                <i className="fas fa-users"></i>
                <div className="insight-content">
                  <h5>Customer Actions</h5>
                  <p>Customers took {(analyticsData.calls + analyticsData.directions + analyticsData.websiteVisits).toLocaleString()} total actions from your listing.</p>
                </div>
              </div>
              
              <div className="insight-card">
                <i className="fas fa-trophy"></i>
                <div className="insight-content">
                  <h5>Review Performance</h5>
                  <p>Maintaining a {analyticsData.reviews.average.toFixed(1)} star rating across {analyticsData.reviews.total} reviews.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="loading-analytics">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading analytics data...</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className="google-business-loading">Loading Google Business settings...</div>;
  }

  return (
    <div className="google-business-container">
      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}
      
      <div className="google-business-header">
        <h2>Google Business & Analytics</h2>
        <p>Manage your Google Business Profile and track your online presence</p>
      </div>
      
      <div className="google-business-nav">
        <button
          className={`nav-btn ${activeSection === 'pixels' ? 'active' : ''}`}
          onClick={() => setActiveSection('pixels')}
        >
          <i className="fas fa-code"></i>
          Tracking Pixels
        </button>
        <button
          className={`nav-btn ${activeSection === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveSection('profile')}
        >
          <i className="fas fa-building"></i>
          Business Profile
        </button>
        <button
          className={`nav-btn ${activeSection === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveSection('analytics')}
        >
          <i className="fas fa-chart-line"></i>
          Analytics
        </button>
      </div>
      
      <div className="google-business-content">
        {activeSection === 'pixels' && renderPixelsSection()}
        {activeSection === 'profile' && renderProfileSection()}
        {activeSection === 'analytics' && renderAnalyticsSection()}
      </div>
    </div>
  );
};